import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action} from '@ngrx/store';
import {concat, Observable, of} from 'rxjs';
import {catchError, mergeMap} from 'rxjs/operators';
import {ChallengeBuilderApiService} from '../../api/challenge-builder-api.service';
import {createCallbackActions, emitErrorActions} from '../store.utils';
import {
  AddAlertAction,
  AddRewardAction,
  AddSalvageAction,
  ChallengeBuilderActionType,
  ChallengeGoalAction,
  ChallengeMetricsAction,
  ChallengeObjectivesAction,
  ChallengeRefineGoalAction,
  ChallengeScheduleAction,
  ChallengeTargetsAction,
  ChallengeTasksAction,
  DeleteAlertAction,
  DeleteRewardAction,
  DeleteSalvageAction,
  GetChallengeBuilderProjectAction,
  GetChallengeBuilderPromptsAction,
  SetChallengeBuilderLoadingAction,
  SetChallengeBuilderPromptsAction,
  StartChallengeAction,
  StopChallengeAction,
  UpdateAlertAction,
  UpdateChallengeBuilderProjectAction,
  UpdateChallengeBuilderPromptsAction,
  UpdateRewardAction,
  UpdateSalvageAction,
  ToggleAiAction,
  UpdateTaskIconAction,
} from './challenge-builder.action';

@Injectable()
export class ChallengeBuilderEffects {
  public getChallengeBuilderProject$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetChallengeBuilderProjectAction>(ChallengeBuilderActionType.GET_CHALLENGE_BUILDER_PROJECT),
      mergeMap(action => {
        const {projectId, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.getChallengeBuilderProject(projectId).pipe(
          mergeMap(project => [
            new UpdateChallengeBuilderProjectAction({cbProject: project}),
            new SetChallengeBuilderLoadingAction({loading: false}),
            ...createCallbackActions(onSuccess, project),
          ]),
          catchError(error =>
            concat(of(new SetChallengeBuilderLoadingAction({loading: false})), emitErrorActions(error, onFailure))
          )
        );
      })
    )
  );

  public challengeRefineGoal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ChallengeRefineGoalAction>(ChallengeBuilderActionType.CHALLENGE_REFINE_GOAL),
      mergeMap(action => {
        const {projectId, initialGoal, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.refineGoal(projectId, initialGoal).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public challengeGoal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ChallengeGoalAction>(ChallengeBuilderActionType.CHALLENGE_GOAL),
      mergeMap(action => {
        const {projectId, goals, goalAltered, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateChallengeGoal(projectId, goals, goalAltered).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public challengeObjectives$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ChallengeObjectivesAction>(ChallengeBuilderActionType.CHALLENGE_OBJECTIVES),
      mergeMap(action => {
        const {projectId, objectives, objectivesAltered, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateChallengeObjectives(projectId, objectives, objectivesAltered).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public challengeTasks$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ChallengeTasksAction>(ChallengeBuilderActionType.CHALLENGE_TASKS),
      mergeMap(action => {
        const {projectId, tasks, tasksAltered, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateChallengeTasks(projectId, tasks, tasksAltered).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public challengeMetrics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ChallengeMetricsAction>(ChallengeBuilderActionType.CHALLENGE_METRICS),
      mergeMap(action => {
        const {projectId, metrics, metricsAltered, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateChallengeMetrics(projectId, metrics, metricsAltered).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public challengeTargets$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ChallengeTargetsAction>(ChallengeBuilderActionType.CHALLENGE_TARGETS),
      mergeMap(action => {
        const {projectId, targets, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateChallengeTargets(projectId, targets).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public challengeSchedule$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ChallengeScheduleAction>(ChallengeBuilderActionType.CHALLENGE_SCHEDULE),
      mergeMap(action => {
        const {projectId, tasks, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateChallengeSchedule(projectId, tasks).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public startChallenge$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StartChallengeAction>(ChallengeBuilderActionType.START_CHALLENGE),
      mergeMap(action => {
        const {projectId, launchData, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.startChallenge(projectId, launchData).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public stopChallenge$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StopChallengeAction>(ChallengeBuilderActionType.STOP_CHALLENGE),
      mergeMap(action => {
        const {projectId, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.stopChallenge(projectId).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public getChallengeBuilderPrompts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetChallengeBuilderPromptsAction>(ChallengeBuilderActionType.GET_CHALLENGE_BUILDER_PROMPTS),
      mergeMap(action => {
        const {onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.getAllChallengeBuilderPrompts().pipe(
          mergeMap(prompts => [
            new SetChallengeBuilderPromptsAction({prompts}),
            ...createCallbackActions(onSuccess, prompts),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public updateChallengeBuilderPrompts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateChallengeBuilderPromptsAction>(ChallengeBuilderActionType.UPDATE_CHALLENGE_BUILDER_PROMPTS),
      mergeMap(action => {
        const {prompts, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateChallengeBuilderPrompts(prompts).pipe(
          mergeMap(updatedPrompts => [
            new SetChallengeBuilderPromptsAction({prompts: updatedPrompts}),
            ...createCallbackActions(onSuccess, updatedPrompts),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public addSalvage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<AddSalvageAction>(ChallengeBuilderActionType.ADD_SALVAGE),
      mergeMap(action => {
        const {projectId, salvage, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.addSalvage(projectId, salvage).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public addReward$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<AddRewardAction>(ChallengeBuilderActionType.ADD_REWARD),
      mergeMap(action => {
        const {projectId, reward, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.addReward(projectId, reward).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public addAlert$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<AddAlertAction>(ChallengeBuilderActionType.ADD_ALERT),
      mergeMap(action => {
        const {projectId, alert, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.addAlert(projectId, alert).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public updateSalvage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateSalvageAction>(ChallengeBuilderActionType.UPDATE_SALVAGE),
      mergeMap(action => {
        const {projectId, salvage, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateSalvage(projectId, salvage).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public deleteSalvage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteSalvageAction>(ChallengeBuilderActionType.DELETE_SALVAGE),
      mergeMap(action => {
        const {projectId, salvageId, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.deleteSalvage(projectId, salvageId).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public updateReward$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateRewardAction>(ChallengeBuilderActionType.UPDATE_REWARD),
      mergeMap(action => {
        const {projectId, reward, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateReward(projectId, reward).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public deleteReward$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteRewardAction>(ChallengeBuilderActionType.DELETE_REWARD),
      mergeMap(action => {
        const {projectId, rewardId, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.deleteReward(projectId, rewardId).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public toggleAlert$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateAlertAction>(ChallengeBuilderActionType.UPDATE_ALERT),
      mergeMap(action => {
        const {projectId, alert, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateAlert(projectId, alert).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public deleteAlert$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteAlertAction>(ChallengeBuilderActionType.DELETE_ALERT),
      mergeMap(action => {
        const {projectId, alertId, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.deleteAlert(projectId, alertId).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public updateTaskIcon$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateTaskIconAction>(ChallengeBuilderActionType.UPDATE_TASK_ICON),
      mergeMap(action => {
        const {projectId, taskId, icon, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.updateTaskIcon(projectId, taskId, icon).pipe(
          mergeMap(() => [...createCallbackActions(onSuccess)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public toggleAi$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ToggleAiAction>(ChallengeBuilderActionType.TOGGLE_AI),
      mergeMap(action => {
        const {projectId, onSuccess, onFailure} = action.payload;

        return this.challengeBuilderApiService.toggleAi(projectId).pipe(
          mergeMap(cbProject => [
            new UpdateChallengeBuilderProjectAction({cbProject}),
            ...createCallbackActions(onSuccess, cbProject),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private challengeBuilderApiService: ChallengeBuilderApiService
  ) {}
}
